import {
    beerify,
    debeerify,
    setUserProfileServerError,
    UserProfileFields,
    useSetTimeout,
    type UserFormFields,
    FormSubmitButton,
} from "@keycloak/keycloak-ui-shared";
import {
    getSupportedLocales,
    Page,
    type UserProfileAttributeMetadata as BaseUserProfileAttributeMetadata,
    type UserProfileMetadata as BaseUserProfileMetadata,
    useAccountAlerts,
    usePromise,
} from "@keycloak/keycloak-account-ui";
import {
    ActionGroup,
    Alert,
    AlertVariant,
    Button,
    ExpandableSection,
    Form,
    Spinner,
} from "@patternfly/react-core";
import { ExternalLinkSquareAltIcon } from "@patternfly/react-icons";
import { useState } from "react";
import {
    type ErrorOption,
    type UseFormReturn,
    useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useEnvironmentWithDonorService } from '../../context';
import {
    getPersonalInfo,
    savePersonalInfo,
    type UserRepresentation,
} from "../../api/methods";
import { countries } from "../../utils/countries.ts";

const ALERT_DELAY = 1000;
const formId = 'ape-personalinfo-form';

export function PersonalInfoPage () {
    const { t } = useTranslation();
    const context = useEnvironmentWithDonorService();

    const [userProfileMetadata, setUserProfileMetadata] =
        useState<UserProfileMetadata>();
    const [supportedLocales, setSupportedLocales] = useState<string[]>([]);

    const form = useForm<UserRepresentation>({
        mode: "onChange",
        shouldUseNativeValidation: true,
    });
    const { handleSubmit, reset, setValue, setError, formState } = form;

    const { addAlert } = useAccountAlerts();
    const setTimeout = useSetTimeout();

    usePromise(
        (signal) =>
            Promise.all([
                getPersonalInfo({ signal, context }),
                getSupportedLocales({ signal, context }),
            ]),
        ([personalInfo, supportedLocales]) => {
            const data = {
                ...personalInfo,
                userProfileMetadata: extendUserProfileMetadata(personalInfo.userProfileMetadata),
            }
            setUserProfileMetadata(data.userProfileMetadata);
            setSupportedLocales(supportedLocales);
            reset(data);
            Object.entries(data.attributes || {})
                .forEach(
                    ([k, v]) => setValue(`attributes[${beerify(k)}]` as keyof UserRepresentation, v),
                );
            if (personalInfo.attributes.addressUndeliverable) setTimeout(
                () => addAlert(
                    t("addressUndeliverableAlertTitle"),
                    AlertVariant.warning,
                    t("addressUndeliverableAlertDesc"),
                ),
                ALERT_DELAY,
            );
        },
        [setTimeout]
    );

    const onSubmit = async (user: UserRepresentation) => {
        // For whatever reason, react-hook-form doesn’t validate the form, so we trigger native validation before submitting.
        const formEl = document.getElementById(formId) as HTMLFormElement;
        if (!formEl.checkValidity()) {
            formEl.reportValidity();
            addAlert(t("Error"), AlertVariant.danger, t("UserError"));
            return;
        }
        try {
            const attributes = Object.fromEntries(
                Object.entries(user.attributes || {})
                    .map(([k, v]) => [debeerify(k), v]),
            );
            await savePersonalInfo(context, { ...user, attributes });
            const locale = attributes["locale"]?.toString();
            if (locale) {
                window.dispatchEvent(
                    new CustomEvent("languageChanged", { detail: { language: locale } }),
                );
            }
            void context.keycloak.updateToken();
            addAlert(t("accountUpdatedMessage"));
        } catch (error) {
            addAlert(t("accountUpdatedError"), AlertVariant.danger);

            setUserProfileServerError(
                { responseData: { errors: error as any } },
                (name: keyof UserRepresentation, error: unknown) => setError(name, error as ErrorOption),
                t,
            );
        }
    };

    if (!userProfileMetadata) {
        return <Spinner />;
    }

    const allFieldsReadOnly = () =>
        userProfileMetadata?.attributes?.every(({ readOnly }) => readOnly)

    const {
        updateEmailFeatureEnabled,
        updateEmailActionEnabled,
        isRegistrationEmailAsUsername,
        isEditUserNameAllowed,
    } = context.environment.features;

    const shouldRenderUpdateEmailButton = Boolean(
        updateEmailFeatureEnabled &&
        updateEmailActionEnabled &&
        (!isRegistrationEmailAsUsername || isEditUserNameAllowed)
    );

    return (
        <Page title={t("personalInfoHtmlTitle")} description={t("personalSubMessage")}>
            <Form id={formId} isHorizontal onSubmit={handleSubmit(onSubmit)}>
                <UserProfileFields
                    form={form as UseFormReturn<UserFormFields>}
                    userProfileMetadata={userProfileMetadata}
                    supportedLocales={supportedLocales}
                    currentLocale={context.environment.locale}
                    t={t}
                    renderer={
                        (attribute) =>
                            attribute.name === 'email' && shouldRenderUpdateEmailButton
                                ? (
                                    <Button
                                        id="update-email-btn"
                                        variant="link"
                                        onClick={() => context.keycloak.login({action: "UPDATE_EMAIL"})}
                                        icon={<ExternalLinkSquareAltIcon />}
                                        iconPosition="right"
                                    >
                                        {t("updateEmail")}
                                    </Button>
                                )
                                : undefined
                    }
                />

                {!allFieldsReadOnly() && (
                    <ActionGroup>
                        <FormSubmitButton
                            formState={formState}
                            data-testid="save"
                            id="save-btn"
                        >
                            {t("save")}
                        </FormSubmitButton>
                        <Button
                            data-testid="cancel"
                            id="cancel-btn"
                            variant="link"
                            onClick={() => reset()}
                        >
                            {t("cancel")}
                        </Button>
                    </ActionGroup>
                )}
                {context.environment.features.deleteAccountAllowed && (
                    <ExpandableSection
                        data-testid="delete-account"
                        toggleText={t("deleteAccount")}
                    >
                        <Alert
                            isInline
                            title={t("deleteAccount")}
                            variant="danger"
                            actionLinks={
                                <Button
                                    id="delete-account-btn"
                                    variant="danger"
                                    onClick={() =>
                                        context.keycloak.login({
                                            action: "delete_account",
                                        })
                                    }
                                    className="delete-button"
                                >
                                    {t("delete")}
                                </Button>
                            }
                        >
                            {t("deleteAccountWarning")}
                        </Alert>
                    </ExpandableSection>
                )}
            </Form>
        </Page>
    );
}

type UserProfileAttributeMetadata = BaseUserProfileAttributeMetadata & {
    group?: 'DonorNumber' | 'Names' | 'Email' | 'Address' | 'Phone';
    annotations?: {
        [key: string]: unknown
        inputTypePlaceholder?: string
        inputType?:
            | 'html5-email'
            | 'html5-tel'
            | 'html5-url'
            | 'html5-number'
            | 'html5-range'
            | 'html5-datetime-local'
            | 'html5-date'
            | 'html5-month'
            | 'html5-week'
            | 'html5-time'
            | 'text'
            | 'textarea'
            | 'select'
            | 'select-radiobuttons'
            | 'multiselect'
            | 'multiselect-checkboxes'
        inputOptionLabels?: Record<string, string>
    };
}

type UserProfileGroupMetadata = {
    name: string;
    displayHeader: string;
    displayDescription: string;
}

type UserProfileMetadata = BaseUserProfileMetadata & {
    attributes: UserProfileAttributeMetadata[];
    groups?: UserProfileGroupMetadata[];
}

function extendUserProfileMetadata(orig: Partial<UserProfileMetadata> = {}): UserProfileMetadata {
    const extraAttributes: UserProfileAttributeMetadata[] = [
        {
            displayName: '${phoneNumber}',
            group: 'Phone',
            multivalued: false,
            name: 'phone',
            readOnly: false,
            required: false,
            defaultValue: '',
            validators: {},
            annotations: {
                inputTypePlaceholder: '${phoneFormPlaceholder}',
                inputType: 'html5-tel',
            },
        },
        {
            displayName: '${dwellingFormLabel}',
            group: 'Address',
            multivalued: false,
            name: 'dwelling',
            readOnly: false,
            required: false,
            defaultValue: '',
            validators: {},
            annotations: {
                inputTypePlaceholder: '${dwellingFormPlaceholder}',
            },
        },
        {
            displayName: '${residenceFormLabel}',
            group: 'Address',
            multivalued: false,
            name: 'residence',
            readOnly: false,
            required: false,
            defaultValue: '',
            validators: {},
            annotations: {
                inputTypePlaceholder: '${residenceFormPlaceholder}',
            },
        },
        {
            displayName: '${localityFormLabel}',
            group: 'Address',
            multivalued: false,
            name: 'locality',
            readOnly: false,
            required: false,
            defaultValue: '',
            validators: {},
            annotations: {
                inputTypePlaceholder: '${localityFormPlaceholder}',
            },
        },
        {
            displayName: '${streetFormLabel}',
            group: 'Address',
            multivalued: false,
            name: 'street',
            readOnly: false,
            required: false,
            defaultValue: '',
            validators: {},
            annotations: {
                inputTypePlaceholder: '${streetFormPlaceholder}',
            },
        },
        {
            displayName: '${postcodeFormLabel}',
            group: 'Address',
            multivalued: false,
            name: 'postcode',
            readOnly: false,
            required: true,
            defaultValue: '',
            validators: {},
            annotations: {
                inputTypePlaceholder: '${postcodeFormPlaceholder}',
            },
        },
        {
            displayName: '${cityFormLabel}',
            group: 'Address',
            multivalued: false,
            name: 'city',
            readOnly: false,
            required: true,
            defaultValue: '',
            validators: {},
            annotations: {
                inputTypePlaceholder: '${cityFormPlaceholder}',
            },
        },
        {
            displayName: '${country}',
            group: 'Address',
            multivalued: false,
            name: 'country',
            readOnly: false,
            required: true,
            defaultValue: 'FR',
            validators: {
                options: {
                    options: Object.keys(countries),
                }
            },
            annotations: {
                inputType: 'select',
                inputOptionLabels: countries,
            },
        },
    ];
    return {
        ...orig,
        attributes: [
            ...extraAttributes,
            ...(orig.attributes ?? []),
        ],
    };
}
